import React, { FC, useEffect, useReducer } from 'react'
import { MediaControlsContext, MediaStateContext } from './context'
import { reducer, getInitialState } from './reducer'
import type { MediaControls, MediaProviderProps } from './types'

const MediaProvider: FC<React.PropsWithChildren<MediaProviderProps>> = ({
  children,
  defaultState = getInitialState(),
}) => {
  const autoPlay =
    !!(React.isValidElement(children) && children.props.autoPlay) || false
  const [state, dispatch] = useReducer(reducer, {
    ...getInitialState(autoPlay),
    ...defaultState,
  })

  const controls: MediaControls = {
    changeVolume: (volumeLevel) =>
      dispatch({ type: 'changeVolume', payload: volumeLevel }),
    onSeek: (time) => dispatch({ type: 'setCurrentTime', payload: time }),
    setCurrentTime: (time) =>
      dispatch({ type: 'setCurrentTime', payload: time }),
    setVideoPlayFromTime: (time) =>
      dispatch({ type: 'setVideoPlayFromTime', payload: time }),
    toggleMuted: () =>
      dispatch({ type: 'toggleMuted', payload: !state.isMuted }),
    togglePlaying: () =>
      dispatch({ type: 'togglePlaying', payload: !state.isPlaying }),
    onPlay: () => dispatch({ type: 'togglePlaying', payload: true }),
    onEnded: () => dispatch({ type: 'togglePlaying', payload: false }),
    onMetadataLoaded: ({ duration }) => {
      if (duration) {
        dispatch({ type: 'setDuration', payload: duration })
      }
    },
  }

  useEffect(() => {
    dispatch({ type: 'togglePlaying', payload: autoPlay })
  }, [autoPlay])

  return (
    <MediaControlsContext.Provider value={controls}>
      <MediaStateContext.Provider value={state}>
        {children}
      </MediaStateContext.Provider>
    </MediaControlsContext.Provider>
  )
}

export default MediaProvider
